import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  Avatar,
  Divider,
} from '@mui/material';
import {
  ShoppingCart,
  Person,
  Menu as MenuIcon,
  FavoriteBorder,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/logo/logo.png';
import ExpandableSearch from './NavBar/ExpandableSearch';
import LanguageSwitcher from './NavBar/LanguageSwitcher';
import NavigationMenu from './NavBar/NavigationMenu';
import { iconButtonSx } from '../UI/iconButtonSx';
import MobileDrawer from './NavBar/MobileDrawer';
import MiniCartPopover from './cart/MiniCartPopover';
import { useGetNavigation } from '../../hooks/storefront/useGetNavigation';
import { useCartCount } from '../../hooks/storefront/useCartCount';
import { useGetWishlistCount } from '../../hooks/customer/useGetWishlistCount';
import { useAuthContext } from '../../auth/useAuthContext';

const Header = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [miniCartAnchor, setMiniCartAnchor] = useState(null);

  const { isAuthenticated, logout } = useAuthContext();

  // جلب بيانات القائمة الرئيسية من الـ API
  const { isLoading: navigationLoading, navigation } = useGetNavigation();

  // جلب عدد المنتجات في السلة
  const { data: cartCount } = useCartCount();
  // جلب عدد المنتجات في قائمة الرغبات
  const { data: wishlistCount = 0 } = useGetWishlistCount();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(26, 26, 26, 0.95)'
          : theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backdropFilter: 'blur(10px)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 2px 8px rgba(0,0,0,0.3)'
          : '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Toolbar sx={{ minHeight: 70 }}>
        {/* Logo */}
        <Box sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src={logo} alt="logo" height={38} />
        </Box>

        {/* Desktop Links */}
        {!isMobile && (
          <NavigationMenu
            navigation={navigation?.data}
            navigationLoading={navigationLoading}
          />
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ExpandableSearch />

          {!isMobile && <LanguageSwitcher />}

          {isAuthenticated && (
            <IconButton sx={iconButtonSx} onClick={() => navigate('/wishlist')}>
              <Badge badgeContent={wishlistCount} color="error">
                <FavoriteBorder />
              </Badge>
            </IconButton>
          )}

          <IconButton
            sx={iconButtonSx}
            onClick={(e) => {
              // Mobile: behave like the old quick-link to /cart;
              // Desktop: open the popover (no full-page nav).
              if (isMobile) {
                navigate('/cart');
              } else {
                setMiniCartAnchor((prev) => (prev ? null : e.currentTarget));
              }
            }}
          >
            <Badge badgeContent={cartCount} color="error">
              <ShoppingCart />
            </Badge>
          </IconButton>
          <MiniCartPopover
            open={Boolean(miniCartAnchor)}
            anchorEl={miniCartAnchor}
            onClose={() => setMiniCartAnchor(null)}
          />

          {!isMobile && <IconButton sx={iconButtonSx} onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar sx={{
              width: 32,
              height: 32,
              bgcolor: 'transparent',
              border: `1px solid ${theme.palette.divider}`,
              color: theme.palette.text.secondary,
            }}>
              <Person fontSize="small" />
            </Avatar>
          </IconButton>}

          {isMobile && (
            <IconButton sx={iconButtonSx} onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
          )}

        </Box>
      </Toolbar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            mt: 1,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          }
        }}
      >
        {!isAuthenticated ? (
          <>
            <MenuItem onClick={() => navigate('/login')}>{t('navigation.login')}</MenuItem>
            <MenuItem onClick={() => navigate('/register')}>{t('navigation.register')}</MenuItem>
          </>
        ) : (
          <>
            <MenuItem onClick={() => navigate('/profile')}>{t('navigation.profile')}</MenuItem>
            <MenuItem onClick={() => navigate('/orders')}>{t('navigation.orders')}</MenuItem>
            <Divider />
            <MenuItem onClick={logout}>{t('navigation.logout')}</MenuItem>
          </>
        )}
      </Menu>
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isAuthenticated={isAuthenticated}
      />

    </AppBar>
  );
};

export default Header;
